<template>
  <div id="volume-buys" class="align-start fill-height">
    <i-overlay v-if="loading" text="Loading volume buys..." />

    <v-row v-else class="g-container-large" no-gutters>
      <v-dialog
        v-if="dialog"
        v-model="dialog"
        max-width="min(96vw, 1280px)"
        persistent
        @click:outside="reset"
        @keydown.esc="reset"
      >
        <i-edit-volume-promotion
          :family-groups="enrichedFamilyGroups"
          :pricing-regions="pricingRegions"
          :products-loading="productsLoading"
          :products="enrichedProducts"
          :volume-promotion="editableVolumePromotion"
          @cancel="reset"
          @ensure-products="ensureDialogProducts"
          @save="reload"
          @search-products="searchDialogProducts"
        ></i-edit-volume-promotion>
      </v-dialog>

      <v-dialog
        v-if="confirmationDialog"
        v-model="confirmationDialog"
        width="400px"
        @click:outside="resetDeletion"
        @keydown.esc="resetDeletion"
      >
        <i-confirmation-dialog
          text="Confirm deletion of this Volume Buy"
          action-text="Delete"
          :submitting="deletingVolumePromotion"
          @cancel="resetDeletion"
          @confirm="confirmDeletion"
        />
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template>
          <v-row>
            <v-col cols="12" md="3" lg="2">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary g-full-width"
                @click="editVolumePromotion()"
              >
                Add Volume Buy
              </v-btn>
            </v-col>
            <v-col cols="12" md="3" lg="3" v-if="!$vuetifyDisplay.smAndDown">
              <v-btn
                large
                rounded
                class="i-btn-block-primary g-full-width"
                :to="{ name: 'volume-buys-multi-upload' }"
              >
                Add Multiple Volume Buys
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="data-maintenance/volume-buys"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'ACTIVE' }"
                @click="selectedStatus = 'ACTIVE'"
              >
                Active
              </v-btn>
            </v-col>
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': selectedStatus === 'UPCOMING'
                }"
                @click="selectedStatus = 'UPCOMING'"
              >
                Upcoming
              </v-btn>
            </v-col>
            <v-col cols="6" md="3" lg="2" v-if="dataApprovalRequired">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': selectedStatus === 'AWAITING_APPROVAL'
                }"
                @click="selectedStatus = 'AWAITING_APPROVAL'"
              >
                <v-badge
                  color="accent"
                  :content="volumePromotionsRequiringAmendmentsCount"
                  right
                  :value="volumePromotionsRequiringAmendmentsCount"
                  >Awaiting Approval</v-badge
                >
              </v-btn>
            </v-col>
            <v-col cols="6" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'PAST' }"
                @click="selectedStatus = 'PAST'"
                >Past</v-btn
              >
            </v-col>
            <v-spacer></v-spacer>
            <v-col
              class="pl-lg-8 promotion-table-actions"
              cols="12"
              lg="4"
              :offset-lg="dataApprovalRequired ? 0 : 2"
            >
              <div class="promotion-table-actions__inner">
                <i-local-filter
                  :filterPlaceholder="'Search by Target Type, Target(s), Family Group, Product, or Volume Buy Name'"
                  @filter="localFilter"
                />
                <i-table-refresh-button
                  :loading="tableLoading"
                  @refresh="refreshCurrentTable"
                />
              </div>
            </v-col>
          </v-row>
        </template>

        <template>
          <v-row>
            <v-col cols="12">
              <template
                v-if="
                  !tableLoading && filteredVolumePromotionsByStatus.length === 0
                "
              >
                <i-empty-results hideButton :title="emptyFilteredResultsText" />
              </template>

              <template v-else>
                <template v-if="$vuetifyDisplay.smAndDown">
                  <div
                    v-for="item in filteredVolumePromotionsByStatus"
                    :key="item.id"
                    class="mt-0 pa-0"
                  >
                    <v-card class="rounded-2 px-2 py-1 mb-6">
                      <template v-if="selectedStatus === 'AWAITING_APPROVAL'">
                        <v-row dense no-gutters>
                          <v-col cols="12">
                            <div
                              v-if="
                                item.approval &&
                                item.approval.status === 'AWAITING_APPROVAL'
                              "
                              class="g-bold g-border-radius-s i-status-block-blue px-2 py-2 my-1 text-center"
                            >
                              STATUS: AWAITING APPROVAL
                              <template v-if="item.toBeDeleted"
                                >FOR DELETION</template
                              >
                            </div>
                            <div
                              v-if="
                                item.approval &&
                                item.approval.status === 'REJECTED'
                              "
                              class="g-bold g-border-radius-s i-status-block-orange px-2 py-2 my-1 text-center"
                            >
                              STATUS: AMENDMENTS REQUESTED
                            </div>
                          </v-col>
                        </v-row>
                      </template>
                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span
                            ><strong>{{ headers[0].text }}</strong></span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip
                            v-if="item.targetBannerGroups"
                            small
                            color="accent"
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                            ><strong>BANNER</strong></v-chip
                          >
                          <v-chip
                            v-else-if="item.targetMultiStoreOperators"
                            small
                            color="primary"
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                            ><strong>MSO</strong></v-chip
                          >
                          <v-chip
                            v-else-if="item.targetStores"
                            small
                            color="secondary"
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                            ><strong>{{
                              isILR ? 'CUSTOMERS' : 'RETAILERS'
                            }}</strong></v-chip
                          >
                          <v-chip
                            v-else
                            small
                            color="green"
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                            ><strong>NATIONAL</strong></v-chip
                          >
                        </v-col>
                      </v-row>

                      <v-divider class="my-2"></v-divider>

                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[1].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span
                            v-if="item.targetBannerGroups"
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                          >
                            <span
                              v-for="item in item.targetBannerGroups"
                              :key="item.bannerGroupId"
                            >
                              {{ item.bannerGroupName }}
                              <br />
                            </span>
                          </span>
                          <span
                            v-else-if="item.targetMultiStoreOperators"
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                          >
                            <span
                              v-for="item in item.targetMultiStoreOperators"
                              :key="item.multiStoreOperatorId"
                            >
                              {{ item.multiStoreOperatorName }}
                              <br />
                            </span>
                          </span>
                          <span
                            v-else-if="item.targetStores"
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                          >
                            <span
                              v-for="item in item.targetStores"
                              :key="item.storeId"
                            >
                              {{ item.storeName }}
                              <br />
                            </span>
                          </span>
                          <span v-else> - </span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[2].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span
                            v-if="item.volumePromotionType === 'FAMILY_GROUP'"
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                          >
                            <v-icon
                              class="i-promo-type-icon i-promo-type-icon-family-group"
                              small
                            >
                              mdi-alpha-f-circle-outline
                            </v-icon>
                            <span
                              class="i-tooltip-outer-text"
                              v-tooltip="{
                                html: `familyGroupProductsToolTipContent-${item.id}`
                              }"
                            >
                              Family Group
                            </span>
                            <span
                              :id="`familyGroupProductsToolTipContent-${item.id}`"
                            >
                              <strong>Family Group</strong><br />
                              <span
                                v-for="productName in item.familyGroupProducts"
                                :key="productName"
                              >
                                {{ productName }}<br />
                              </span>
                            </span>
                          </span>

                          <span
                            v-else-if="
                              item.volumePromotionType === 'SINGLE_PRODUCT'
                            "
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                          >
                            <v-icon
                              class="i-promo-type-icon i-promo-type-icon-single-product"
                              small
                            >
                              mdi-alpha-p-circle-outline
                            </v-icon>
                            Single Product
                          </span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span
                            v-if="item.volumePromotionType === 'FAMILY_GROUP'"
                            >{{ headers[3].textFamily }}</span
                          >
                          <span v-else>{{ headers[3].textSingleProduct }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>
                            <router-link
                              v-if="!item.familyGroupDeleted"
                              :to="getLinkRoute(item)"
                              class="i-text-link"
                              :class="[
                                !!item.toBeDeleted ? 'p-strike-through' : ''
                              ]"
                              >{{ item.displayName }}
                            </router-link>
                            <span v-else>{{ item.displayName }} (DELETED)</span>
                          </span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[4].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                            >{{ item.name }}</span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[5].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                            >{{
                              $filters.formatDateTime12h(item.startDateTime)
                            }}</span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[6].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span
                            :class="[
                              !!item.toBeDeleted ? 'p-strike-through' : ''
                            ]"
                            >{{
                              $filters.formatDateTime12h(item.endDateTime)
                            }}</span
                          >
                        </v-col>
                      </v-row>

                      <template v-if="!(item.status === 'PAST')">
                        <v-divider class="my-2"></v-divider>

                        <v-row no-gutters>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-blue g-full-width"
                              :disabled="
                                item.status === 'PAST' ||
                                item.familyGroupDeleted ||
                                item.toBeDeleted ||
                                item.hasPendingEndDateAmendment ||
                                (item.approval &&
                                  item.approval.status === 'AWAITING_APPROVAL')
                              "
                              @click="editVolumePromotion(item)"
                            >
                              <span>Edit Volume Buy</span>
                              <v-icon small class="ml-1"
                                >mdi-circle-edit-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-red g-full-width"
                              :disabled="
                                (item.status !== 'UPCOMING' &&
                                  item.status !== 'AWAITING_APPROVAL') ||
                                item.familyGroupDeleted ||
                                item.toBeDeleted ||
                                (item.active &&
                                  item.status === 'AWAITING_APPROVAL')
                              "
                              @click="startDeletion(item.id)"
                            >
                              <span>Delete Volume Buy</span>
                              <v-icon small class="ml-1"
                                >mdi-delete-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>
                    </v-card>
                  </div>
                </template>

                <template v-else>
                  <v-data-table
                    class="pt-2 elevation-1 i-data-table"
                    v-model:expanded="expandedVolumePromotion"
                    :headers="headersByStatus"
                    :items="filteredVolumePromotionsByStatus"
                    :items-per-page="-1"
                    :loading="tableLoading"
                    :no-data-text="emptyFilteredResultsText"
                    :no-results-text="emptyFilteredResultsText"
                    item-key="id"
                    show-expand
                  >
                    <template #[`item.targetType`]="{ item }">
                      <v-chip
                        v-if="item.targetBannerGroups"
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                        small
                        color="accent"
                        >BANNER</v-chip
                      >
                      <v-chip
                        v-else-if="item.targetMultiStoreOperators"
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                        small
                        color="primary"
                        >MSO</v-chip
                      >
                      <v-chip
                        v-else-if="item.targetStores"
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                        small
                        color="secondary"
                        >{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</v-chip
                      >

                      <v-chip
                        v-else
                        small
                        color="green"
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                        ><strong>ALL ZONES</strong></v-chip
                      >
                    </template>

                    <template #[`item.targetValue`]="{ item }">
                      <span
                        v-if="item.targetBannerGroups"
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                      >
                        <span
                          v-for="item in item.targetBannerGroups"
                          :key="item.bannerGroupId"
                        >
                          {{ item.displayName }}
                          <br />
                        </span>
                      </span>
                      <span
                        v-else-if="item.targetMultiStoreOperators"
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                      >
                        <span
                          v-for="item in item.targetMultiStoreOperators"
                          :key="item.multiStoreOperatorId"
                        >
                          {{ item.displayName }}
                          <br />
                        </span>
                      </span>
                      <span
                        v-else-if="item.targetStores"
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                      >
                        <span
                          v-for="item in item.targetStores"
                          :key="item.storeId"
                        >
                          {{ item.displayName }}
                          <br />
                        </span>
                      </span>
                      <span v-else> - </span>
                    </template>

                    <template v-slot:[`item.supplierItemCodes`]="{ item }">
                      <span
                        v-if="
                          item.supplierItemCodes &&
                          item.supplierItemCodes.length
                        "
                      >
                        <span
                          v-for="(code, i) in item.supplierItemCodes"
                          :key="i"
                        >
                          {{ code }}<br />
                        </span>
                      </span>
                      <span v-else>-</span>
                    </template>

                    <template v-slot:[`item.volumePromotionType`]="{ item }">
                      <div v-if="item.volumePromotionType === 'FAMILY_GROUP'">
                        <v-icon
                          class="i-promo-type-icon i-promo-type-icon-family-group"
                          small
                          v-tooltip.left="{
                            html: `familyGroupProductsToolTipContent-${item.id}`
                          }"
                        >
                          mdi-alpha-f-circle-outline
                        </v-icon>
                        <span
                          :id="`familyGroupProductsToolTipContent-${item.id}`"
                        >
                          <strong>Family Group</strong><br />
                          <span
                            v-for="productName in item.familyGroupProducts"
                            :key="productName"
                          >
                            {{ productName }}<br />
                          </span>
                        </span>
                      </div>
                      <v-icon
                        v-if="item.volumePromotionType === 'SINGLE_PRODUCT'"
                        class="i-promo-type-icon i-promo-type-icon-single-product"
                        small
                        v-tooltip.left="'Single Product'"
                      >
                        mdi-alpha-p-circle-outline
                      </v-icon>
                    </template>

                    <template #[`item.displayName`]="{ item }">
                      <router-link
                        v-if="!item.familyGroupDeleted"
                        :to="getLinkRoute(item)"
                        class="i-text-link"
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                        >{{ item.displayName }}
                      </router-link>
                      <span v-else>{{ item.displayName }} (DELETED)</span>
                    </template>

                    <template #[`item.name`]="{ item }">
                      <span
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                        >{{ item.name }}</span
                      >
                    </template>

                    <template #[`item.startDate`]="{ item }">
                      <span
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                        >{{
                          $filters.formatDateTime12h(item.startDateTime)
                        }}</span
                      >
                    </template>

                    <template #[`item.endDate`]="{ item }">
                      <span
                        :class="[!!item.toBeDeleted ? 'p-strike-through' : '']"
                        >{{
                          $filters.formatDateTime12h(item.endDateTime)
                        }}</span
                      >
                    </template>

                    <template #[`item.approvalStatus`]="{ item }">
                      <template v-if="item.approval">
                        <p
                          v-if="item.approval.status === 'AWAITING_APPROVAL'"
                          class="g-bold g-border-radius-s i-status-block-blue px-2 py-2 my-1 text-center"
                          v-resize-text="{
                            minFontSize: '13px',
                            maxFontSize: '15px'
                          }"
                        >
                          AWAITING APPROVAL
                          <template v-if="item.toBeDeleted"
                            >FOR DELETION</template
                          >
                        </p>
                        <p
                          v-if="item.approval.status === 'REJECTED'"
                          class="g-bold g-border-radius-s i-status-block-orange px-2 py-2 my-1 text-center"
                          v-resize-text="{
                            minFontSize: '13px',
                            maxFontSize: '15px'
                          }"
                        >
                          AMENDMENTS REQUESTED
                        </p>
                      </template>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <v-btn
                        color="secondary"
                        :disabled="
                          item.status === 'PAST' ||
                          item.familyGroupDeleted ||
                          item.toBeDeleted ||
                          item.hasPendingEndDateAmendment ||
                          (item.approval &&
                            item.approval.status === 'AWAITING_APPROVAL')
                        "
                        icon
                        v-tooltip="'Edit'"
                        @click.stop="editVolumePromotion(item)"
                      >
                        <v-icon>mdi-circle-edit-outline</v-icon>
                      </v-btn>

                      <v-btn
                        icon
                        color="error"
                        :disabled="
                          (item.status !== 'UPCOMING' &&
                            item.status !== 'AWAITING_APPROVAL') ||
                          item.familyGroupDeleted ||
                          item.toBeDeleted ||
                          (item.active && item.status === 'AWAITING_APPROVAL')
                        "
                        v-tooltip="'Delete'"
                        @click="startDeletion(item.id)"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>

                    <template #expanded-item="{ headers, item }">
                      <td class="pt-6 pb-4 px-8" :colspan="headers.length">
                        <p class="g-bold g-heading-xs text-center my-2">
                          TIERS BY PRICING REGION
                        </p>
                        <v-simple-table>
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="text-center" style="width: 15%">
                                  {{
                                    isServiceProduct(item)
                                      ? 'PRICE (EX TAX)'
                                      : tierHeaders[0].text
                                  }}
                                </th>
                                <th class="text-center" style="width: 15%">
                                  {{
                                    isServiceProduct(item)
                                      ? 'NO. OF SERVICE'
                                      : tierHeaders[1].text
                                  }}
                                </th>
                                <th class="text-center" style="width: 15%">
                                  {{
                                    isServiceProduct(item)
                                      ? 'AMOUNT OFF (EX TAX)'
                                      : tierHeaders[2].text
                                  }}
                                </th>
                                <th
                                  v-if="selectedStatus !== 'PAST'"
                                  class="text-center"
                                  style="width: 15%"
                                >
                                  <template
                                    v-if="item.variableWeightTargetInKg"
                                    >{{
                                      tierHeaders[3].textVariableWeight
                                    }}</template
                                  >
                                  <template v-else>{{
                                    tierHeaders[3].textFixedWeight
                                  }}</template>
                                </th>
                                <th
                                  v-if="
                                    selectedStatus !== 'PAST' && !isUSCountry
                                  "
                                  class="text-center"
                                  style="width: 20%"
                                >
                                  <template
                                    v-if="item.variableWeightTargetInKg"
                                    >{{
                                      tierHeaders[4].textVariableWeight
                                    }}</template
                                  >
                                  <template v-else>{{
                                    tierHeaders[4].textFixedWeight
                                  }}</template>
                                </th>
                                <th
                                  v-if="!isServiceProduct(item) && !isUSCountry"
                                  class="text-center"
                                  style="width: 20%"
                                >
                                  <template
                                    v-if="item.variableWeightTargetInKg"
                                    >{{
                                      tierHeaders[5].textVariableWeight
                                    }}</template
                                  >
                                  <template v-else>{{
                                    tierHeaders[5].textFixedWeight
                                  }}</template>
                                </th>
                                <th
                                  v-if="
                                    selectedStatus !== 'PAST' &&
                                    !isServiceProduct(item) &&
                                    !isUSCountry
                                  "
                                  class="text-center"
                                  style="width: 20%"
                                >
                                  {{ tierHeaders[6].text }}
                                </th>
                              </tr>
                            </thead>
                            <tbody
                              v-for="zone in item.zonePrices"
                              :key="zone.pricingRegionCode"
                            >
                              <tr>
                                <th
                                  :colspan="visibleTierColumnCount(item)"
                                  class="text-center"
                                >
                                  {{
                                    getRegionDisplayName(zone.pricingRegionCode)
                                  }}
                                </th>
                              </tr>
                              <tr
                                v-for="tier in zone.tiers"
                                :key="tier.numberOfCartons"
                                class="text-center"
                              >
                                <td>
                                  {{ $filters.currency(zone.cartonPriceExTax) }}
                                </td>
                                <td class="p-cartons">
                                  {{ tier.numberOfCartons }}
                                </td>
                                <td>{{ $filters.currency(tier.amountOff) }}</td>
                                <td
                                  v-if="selectedStatus !== 'PAST'"
                                  class="i-td-chip"
                                >
                                  <v-chip class="chip">
                                    <template
                                      v-if="tier.revisedTierCostPerKGExTax"
                                      >{{
                                        $filters.currency(
                                          tier.revisedTierCostPerKGExTax
                                        )
                                      }}</template
                                    >
                                    <template v-else>{{
                                      $filters.currency(
                                        tier.revisedTierUnitPriceExTax
                                      )
                                    }}</template>
                                  </v-chip>
                                </td>
                                <td
                                  v-if="
                                    selectedStatus !== 'PAST' && !isUSCountry
                                  "
                                  class="text-center"
                                >
                                  <template
                                    v-if="tier.revisedTierCostPerKGIncTax"
                                    >{{
                                      $filters.currency(
                                        tier.revisedTierCostPerKGIncTax
                                      )
                                    }}</template
                                  >
                                  <template v-else>{{
                                    $filters.currency(
                                      tier.revisedTierUnitPriceIncTax
                                    )
                                  }}</template>
                                </td>
                                <td
                                  v-if="!isServiceProduct(item) && !isUSCountry"
                                >
                                  {{ $filters.currency(tier.unitRrp) }}
                                </td>
                                <td
                                  v-if="
                                    selectedStatus !== 'PAST' &&
                                    !isServiceProduct(item) &&
                                    !isUSCountry
                                  "
                                  class="i-td-chip"
                                >
                                  <v-chip
                                    class="chip"
                                    :class="{
                                      negative: tier.grossProfitPercentage < 0,
                                      positive: tier.grossProfitPercentage > 0
                                    }"
                                    >{{
                                      tier.grossProfitPercentage || '-'
                                    }}</v-chip
                                  >
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </td>
                    </template>
                  </v-data-table>
                </template>
              </template>

              <i-paginator
                v-if="pagination.totalPages > 1"
                class="mt-6"
                :currentPage="currentPage"
                :pageSize="pageSize"
                :pagination="pagination"
                @paginate="handlePageChange"
              />
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iEditVolumePromotion from '../components/EditVolumePromotion'
import {
  fetchVolumePromotions,
  deleteVolumePromotion
} from '@/services/volumePromotions'
import { findProductsForSupplierAndProductCodes } from '@/services/products'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import iPaginator from '@/components/Paginator'
import iTableRefreshButton from '../components/TableRefreshButton'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'
import { formatRegionDisplayName } from '@/utils/stateDisplay'
import {
  buildUpcomingPriceChangeProductCodeSet,
  familyGroupHasUpcomingPriceChange,
  productHasUpcomingPriceChange
} from '@/modules/dataMaintenance/helper/PromotionProductAvailabilityHelper'

const PROMOTIONS_PAGE_SIZE = 25
const PRODUCT_SEARCH_RESULT_LIMIT = 25

const withZoneCodesWithPrice = (product, pricingRegions) => ({
  ...product,
  zoneCodesWithPrice: Object.keys(product.regionalPriceMap || {}).map((code) =>
    pricingRegions.find((zone) => code === zone.code)
  )
})

export default {
  name: 'volume-buys',

  components: {
    iEmptyResults,
    iInstructions,
    iPaginator,
    iLocalFilter,
    iOverlay,
    iTableRefreshButton,
    iConfirmationDialog,
    iEditVolumePromotion
  },

  props: {
    id: String,
    status: String
  },

  data: () => ({
    dialog: false,
    loading: true,
    tableLoading: false,
    confirmationDialog: false,
    deletingVolumePromotion: false,
    selectedVolumePromotionId: null,
    editableVolumePromotion: {},
    expandedVolumePromotion: [],
    volumePromotions: [],
    filterText: '',
    selectedStatus: 'ACTIVE',
    currentPage: 1,
    pageSize: PROMOTIONS_PAGE_SIZE,
    pageProductsByCode: {},
    pagination: {
      total: 0,
      totalPages: 0
    },
    isInitialLoadComplete: false,
    pendingFocusId: null,
    promotionLoadId: 0,
    productSearchDebouncer: null,
    statusList: [
      { title: 'Active', value: 'ACTIVE' },
      { title: 'Past', value: 'PAST' },
      { title: 'Upcoming', value: 'UPCOMING' }
    ],
    headers: [
      {
        text: 'TARGET TYPE',
        value: 'targetType',
        width: '120',
        align: 'center',
        sortable: false
      },
      {
        text: 'TARGET(S)',
        value: 'targetValue',
        width: '15%',
        sortable: false
      },
      {
        text: 'TYPE',
        value: 'volumePromotionType',
        align: 'center',
        width: '80'
      },
      {
        text: 'SUPPLIER ITEM CODE',
        value: 'supplierItemCodes',
        align: 'center'
      },
      {
        text: 'FAMILY GROUP OR PRODUCT',
        textFamily: 'FAMILY GROUP',
        textSingleProduct: 'PRODUCT',
        value: 'displayName'
      },
      {
        text: 'VOLUME BUY NAME',
        value: 'name',
        width: '400'
      },
      {
        text: 'START DATE',
        value: 'startDate',
        align: 'center',
        width: '140'
      },
      {
        text: 'END DATE',
        value: 'endDate',
        align: 'center',
        width: '180'
      },
      {
        text: 'ACTIONS',
        value: 'actions',
        width: '120',
        align: 'center',
        sortable: false
      }
    ],
    headersAwaitingApproval: [
      {
        text: 'TARGET TYPE',
        value: 'targetType',
        width: '120',
        align: 'center',
        sortable: false
      },
      {
        text: 'TARGET(S)',
        value: 'targetValue',
        width: '15%',
        sortable: false
      },
      {
        text: 'TYPE',
        value: 'volumePromotionType',
        align: 'center',
        width: '80'
      },
      {
        text: 'SUPPLIER ITEM CODE',
        value: 'supplierItemCodes',
        align: 'center'
      },
      {
        text: 'FAMILY GROUP OR PRODUCT',
        textFamily: 'FAMILY GROUP',
        textSingleProduct: 'PRODUCT',
        value: 'displayName'
      },
      {
        text: 'VOLUME BUY NAME',
        value: 'name',
        width: '400'
      },
      {
        text: 'START DATE',
        value: 'startDate',
        align: 'center',
        width: '140'
      },
      {
        text: 'END DATE',
        value: 'endDate',
        align: 'center',
        width: '180'
      },
      {
        text: 'STATUS',
        value: 'approvalStatus',
        align: 'center',
        width: '160'
      },
      {
        text: 'ACTIONS',
        value: 'actions',
        width: '120',
        align: 'center',
        sortable: false
      }
    ],
    tierHeaders: [
      { text: 'CARTON PRICE (EX TAX)' },
      { text: 'NUMBER OF CARTONS' },
      { text: 'AMOUNT OFF CARTON (EX TAX)' },
      {
        textVariableWeight: 'REVISED COST / KG (EX TAX)',
        textFixedWeight: 'REVISED UNIT COST (EX TAX)'
      },
      {
        textVariableWeight: 'REVISED COST / KG (INC TAX)',
        textFixedWeight: 'REVISED UNIT COST (INC TAX)'
      },
      {
        textVariableWeight: 'VOLUME BUY RRP / KG (INC TAX)',
        textFixedWeight: 'VOLUME BUY UNIT RRP (INC TAX)'
      },
      { text: 'GP%' }
    ]
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('products', {
      products: 'products',
      productsLoading: 'loading'
    }),
    ...mapState('familyGroups', ['familyGroups']),
    ...mapState('pricingRegions', ['pricingRegions']),
    ...mapGetters('dataMaintenance', [
      'volumePromotionsRequiringAmendmentsCount'
    ]),
    ...mapGetters('profile', ['dataApprovalRequired']),
    ...mapGetters('tenant', ['isILR', 'tenantCountryRegion']),
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('stores', ['retailerStores']),
    ...mapState('dataMaintenance', ['promotionUpdatedEvent']),

    headersByStatus() {
      return this.selectedStatus === 'AWAITING_APPROVAL'
        ? this.headersAwaitingApproval
        : this.headers
    },

    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },

    isUSCountry() {
      return this.displayRules.isUSCountry
    },

    emptyFilteredResultsText() {
      return `No ${this.selectedStatus.replaceAll('_', ' ')} Volume Buys found`
    },

    upcomingPriceChangeProductCodes() {
      return buildUpcomingPriceChangeProductCodeSet(
        this.products,
        this.familyGroups
      )
    },

    enrichedFamilyGroups() {
      return this.familyGroups.map((f) => {
        const familyGroup = {
          ...f,
          editable: this.volumePromotions.every(
            (v) => v.familyGroup?.id !== f.id
          )
        }

        return {
          ...familyGroup,
          disabled: familyGroupHasUpcomingPriceChange(
            familyGroup,
            this.products
          )
        }
      })
    },

    enrichedProducts() {
      return this.products.map((product) => ({
        ...withZoneCodesWithPrice(product, this.pricingRegions),
        disabled: productHasUpcomingPriceChange(
          product,
          this.familyGroups,
          this.upcomingPriceChangeProductCodes
        )
      }))
    },

    enrichedVolumePromotions() {
      return this.volumePromotions.map((vp) => {
        const enriched = {
          ...vp,
          ...this.extraVolumePromotionInfo(vp),
          product: vp.familyGroup
            ? null
            : this.getProductByCode(vp.productCode),
          supplierItemCodes: this.getSupplierItemCodesForVolumePromotion(vp),
          zonePrices: [...vp.zonePrices].sort((p1, p2) =>
            p1.pricingRegionCode < p2.pricingRegionCode ? -1 : 1
          )
        }

        if (vp.targetBannerGroups) {
          enriched.targetBannerGroups = vp.targetBannerGroups.map((target) => {
            const match = this.bannerGroups.find(
              (bg) => bg.id === target.bannerGroupId
            )
            return {
              ...target,
              displayName: match?.displayName || `${target.bannerGroupName}`
            }
          })
        }

        if (vp.targetMultiStoreOperators) {
          enriched.targetMultiStoreOperators = vp.targetMultiStoreOperators.map(
            (target) => {
              const match = this.multiStoreOperators.find(
                (mso) => mso.id === target.multiStoreOperatorId
              )
              return {
                ...target,
                displayName:
                  match?.displayName || `${target.multiStoreOperatorName}`
              }
            }
          )
        }

        if (vp.targetStores) {
          enriched.targetStores = vp.targetStores.map((target) => {
            const match = this.retailerStores.find(
              (store) => store.id === target.storeId
            )
            return {
              ...target,
              displayName: `${target.storeName} ${
                match?.pricingRegionExternalCode || ''
              }`.trim()
            }
          })
        }

        return enriched
      })
    },

    volumePromotionsByStatus() {
      return this.enrichedVolumePromotions
    },

    filteredVolumePromotionsByStatus() {
      return this.volumePromotionsByStatus
    }
  },

  watch: {
    selectedStatus() {
      this.pendingFocusId = null
      this.currentPage = 1
      this.expandedVolumePromotion = []
      this.reload()
    },
    promotionUpdatedEvent(event) {
      if (event?.promotionType === 'VOLUME_PROMOTION') this.reload()
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('products', ['fetchProducts']),
    ...mapActions('familyGroups', ['fetchFamilyGroups']),
    ...mapActions('dataMaintenance', ['fetchApprovalStatusStatistics']),

    localFilter(filterText) {
      this.filterText = typeof filterText === 'string' ? filterText.trim() : ''
      if (!this.isInitialLoadComplete) return
      this.pendingFocusId = null
      this.currentPage = 1
      this.expandedVolumePromotion = []
      this.reload()
    },

    deleteVolumePromotion(volumePromotionId) {
      this.deletingVolumePromotion = true
      this.addNotification({
        color: 'secondary',
        text: 'Deleting Volume Buy...'
      })

      deleteVolumePromotion(this.selectedSupplierId, volumePromotionId)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Volume Buy deleted!'
          })
          this.reload()
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Volume Buy could not be deleted...'
          })
        })
        .finally(() => {
          this.deletingVolumePromotion = false
          this.resetDeletion()
        })
    },

    editVolumePromotion(volumePromotion = {}) {
      this.editableVolumePromotion = _.cloneDeep(volumePromotion)
      this.dialog = true
      if (!volumePromotion?.id) this.ensureDialogProducts()
    },

    async fetchDialogProducts(query = '') {
      return this.fetchProducts({
        supplierId: this.selectedSupplierId,
        status: 'LIVE',
        page: 0,
        size: PRODUCT_SEARCH_RESULT_LIMIT,
        query: query || undefined
      })
    },

    ensureDialogProducts() {
      if (!this.selectedSupplierId || this.enrichedProducts.length) {
        return Promise.resolve()
      }
      return this.fetchDialogProducts()
    },

    searchDialogProducts(query = '') {
      const normalizedQuery = query.trim()

      if (!normalizedQuery) return this.fetchDialogProducts()

      if (!this.productSearchDebouncer) {
        this.productSearchDebouncer = _.debounce(
          (nextQuery) => this.fetchDialogProducts(nextQuery),
          250
        )
      }

      return this.productSearchDebouncer(normalizedQuery)
    },

    async expandVolumePromotion(id) {
      const volumePromotion = this.enrichedVolumePromotions.find(
        (p) => p.id === +id
      )
      if (!volumePromotion) return

      if (this.selectedStatus !== volumePromotion.status) {
        const wasReady = this.isInitialLoadComplete
        this.isInitialLoadComplete = false
        this.pendingFocusId = +id
        this.selectedStatus = volumePromotion.status || 'ACTIVE'
        await this.reload()
        this.isInitialLoadComplete = wasReady
      }

      const expandedVolumePromotion = this.enrichedVolumePromotions.find(
        (p) => p.id === +id
      )
      this.expandedVolumePromotion = expandedVolumePromotion
        ? [expandedVolumePromotion]
        : []
    },

    extraVolumePromotionInfo(volumePromotion) {
      if (volumePromotion.familyGroup) {
        return {
          displayName: volumePromotion.familyGroup.name,
          familyGroupDeleted: volumePromotion.familyGroup.deleted,
          volumePromotionType: 'FAMILY_GROUP',
          familyGroupProducts: this.getFamilyGroupProducts(
            volumePromotion.familyGroup.products
          )
        }
      }

      const product = this.getProductByCode(volumePromotion.productCode)
      return {
        displayName:
          product?.tradeItemDescription || volumePromotion.productCode || '',
        familyGroupDeleted: false,
        volumePromotionType: 'SINGLE_PRODUCT'
      }
    },

    getFamilyGroupProducts(products = []) {
      return products.map((product) => product.tradeItemDescription)
    },

    isServiceProduct(item) {
      if (item?.volumePromotionType !== 'SINGLE_PRODUCT') return false
      const product = this.getProductByCode(item.productCode)
      return product?.productType === 'SERVICE'
    },

    visibleTierColumnCount(item) {
      let count = 3
      if (this.selectedStatus !== 'PAST') count += 1
      if (!this.isUSCountry && this.selectedStatus !== 'PAST') count += 1
      if (!this.isServiceProduct(item)) {
        if (!this.isUSCountry) count += 1
        if (!this.isUSCountry && this.selectedStatus !== 'PAST') count += 1
      }
      return count
    },

    async getVolumePromotions({ initialLoad = false } = {}) {
      const promotionLoadId = ++this.promotionLoadId
      const pendingFocusId = this.pendingFocusId
      if (initialLoad) this.loading = true
      else this.tableLoading = true
      try {
        const response = await fetchVolumePromotions(this.selectedSupplierId, {
          page: this.currentPage - 1,
          size: this.pageSize,
          query: this.filterText || undefined,
          status: pendingFocusId ? undefined : this.selectedStatus,
          focusId: pendingFocusId || undefined
        })
        const rawVolumePromotions = Array.isArray(response)
          ? response
          : response?.volumePromotions || []
        const pageProductsByCode =
          await this.loadPageProducts(rawVolumePromotions)

        if (promotionLoadId !== this.promotionLoadId) return

        this.pagination = Array.isArray(response)
          ? {
              total: rawVolumePromotions.length,
              totalPages: rawVolumePromotions.length ? 1 : 0
            }
          : response?.pagination || { total: 0, totalPages: 0 }
        this.pendingFocusId = null
        this.pageProductsByCode = pageProductsByCode
        this.volumePromotions = rawVolumePromotions
      } catch {
        if (promotionLoadId !== this.promotionLoadId) return
        this.addNotification({
          color: 'error',
          text: 'Volume Buys could not be reloaded...'
        })
      } finally {
        if (promotionLoadId === this.promotionLoadId) {
          this.loading = false
          this.tableLoading = false
        }
      }
    },

    getProductByCode(productCode) {
      const product =
        this.pageProductsByCode[productCode] ||
        this.products.find((p) => p.productCode === productCode)

      return product
        ? withZoneCodesWithPrice(product, this.pricingRegions)
        : null
    },

    async loadPageProducts(volumePromotions) {
      const productCodes = [
        ...new Set(
          volumePromotions.flatMap((promotion) => {
            if (promotion.familyGroup?.products?.length) {
              return promotion.familyGroup.products.map(
                (product) => product.productCode
              )
            }
            return promotion.productCode ? [promotion.productCode] : []
          })
        )
      ].filter(Boolean)

      if (!productCodes.length) {
        return {}
      }

      const products = await findProductsForSupplierAndProductCodes(
        this.selectedSupplierId,
        productCodes,
        { includeUpcoming: false }
      )

      return products.reduce((acc, product) => {
        acc[product.productCode] = product
        return acc
      }, {})
    },

    getLinkRoute(item) {
      if (item.familyGroup) {
        return {
          name: 'family-groups',
          query: { familyGroupId: item.familyGroup.id }
        }
      }

      const product = this.getProductByCode(item.productCode)
      return {
        name: 'products',
        query: { supplierItemId: product?.supplierItemId }
      }
    },

    getRegionDisplayName(regionCode) {
      return (
        formatRegionDisplayName(
          this.pricingRegions.find((region) => region.code === regionCode),
          this.tenantCountryRegion
        ) || `Unknown region: ${regionCode}`
      )
    },

    async reload({ initialLoad = false } = {}) {
      await Promise.all([
        this.fetchApprovalStatusStatistics(),
        this.getVolumePromotions({ initialLoad })
      ])

      if (
        this.pagination.totalPages > 0 &&
        this.currentPage > this.pagination.totalPages
      ) {
        this.currentPage = this.pagination.totalPages
        return this.reload({ initialLoad })
      }

      this.reset()
    },

    async handlePageChange(page) {
      this.currentPage = page
      this.expandedVolumePromotion = []
      await this.reload()
    },

    async refreshCurrentTable() {
      await this.reload()
    },

    reset() {
      this.editableVolumePromotion = {}
      this.dialog = false
    },

    startDeletion(itemId) {
      this.confirmationDialog = true
      this.selectedVolumePromotionId = itemId
    },

    resetDeletion() {
      this.confirmationDialog = false
      this.selectedVolumePromotionId = null
    },

    confirmDeletion() {
      this.deleteVolumePromotion(this.selectedVolumePromotionId)
    },

    getSupplierItemId(productCode) {
      const product = this.getProductByCode(productCode)
      return product ? product.supplierItemId : 'Unknown'
    },

    getSupplierItemCodesForVolumePromotion(vp) {
      if (vp.familyGroup && Array.isArray(vp.familyGroup.products)) {
        return vp.familyGroup.products
          .map((p) => this.getSupplierItemId(p.productCode))
          .filter(Boolean)
      }

      if (vp.productCode) {
        return [this.getSupplierItemId(vp.productCode)]
      }

      return []
    }
  },

  async mounted() {
    try {
      this.loading = true
      this.fetchFamilyGroups(this.selectedSupplierId)
      this.selectedStatus = this.status?.toUpperCase() || 'ACTIVE'
      this.pendingFocusId = this.id ? Number(this.id) : null
      await this.reload({ initialLoad: true })
      this.isInitialLoadComplete = true
      if (this.id) await this.expandVolumePromotion(this.id)
    } catch {
      this.addNotification({
        color: 'error',
        text: 'Volume Buys data could not be loaded, please try again...'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
#volume-buys {
  .promotion-table-actions__inner {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 42px;
    gap: 10px;
    align-items: stretch;
  }

  .p-cartons {
    font-weight: bold;
    color: $primary-color;
    font-size: 16px;
  }

  .p-strike-through {
    text-decoration: line-through;
  }
}

@media only screen and (min-width: 960px) {
  #volume-buys {
    .p-cartons {
      font-size: 20px;
    }
  }
}
</style>
